import type {
  AdditionalSpaceObservingSession,
  VisitCounselView,
} from '@/entities/visit-counsel/model/visit-counsel.types';
import { hasObservingSessionMovingItemsConfirmed } from '@/entities/visit-counsel/model/observing-session-moving-items';
import { sortAdditionalSpaceObservingSessions } from '@/features/visit-counsel/counsel-progress/model/additionalSpaceObservingOrder';
import { SPACE_TYPE_LABELS } from '@/features/visit-counsel/counsel-progress/model/counsel-progress.constants';

/**
 * 추가공간 촬영 stage 의 picker entry. `MovingItemsFlowAdapter` 에 그대로 넘길 수 있는
 * shape 으로 미리 정리해 둔다.
 *
 * - `canRecordMovingItems === true` 추가공간 (세대창고 등): 짐 picker → 영상 전체 진행.
 * - `canRecordMovingItems === false` 추가공간 (사다리차/계단 등): 영상만 진행 (start([]) 시퀀스).
 */
export interface AdditionalSpacePendingEntry {
  sessionId: string;
  /** 표시 라벨 — `SPACE_TYPE_LABELS[spaceType]` 폴백 */
  label: string;
  /** 추가공간 옵저빙 세션 원본 — picker 카드 아이콘/상태 계산용 */
  session: AdditionalSpaceObservingSession;
  /** 짐 기록 가능 여부 (`MovingItemsFlowAdapter.canRecordMovingItems` 로 그대로 전달) */
  canRecordMovingItems: boolean;
  /** REQUESTED 세션이면 첫 시작(`isFirstStart=true`) — `MovingItemsFlowAdapter` 에 전달 */
  isFirstStart: boolean;
}

/**
 * 추가공간 옵저빙 세션 중 영상 또는 짐 기록이 미완료인 항목만 골라 sorted entry 로 반환.
 *
 * 미완료 기준 (`isCounselReadyForCompletion` 의 추가공간 검사 로직과 정렬):
 *   - 영상 토큰(`session.video`) 이 없음, 또는
 *   - `canRecordMovingItems === true` 인데 짐이 confirmed 가 아님, 또는
 *   - `canRecordMovingItems === true` 인데 movingItem 중 `serviceSet` 누락 항목 존재.
 */
export function buildAdditionalSpacePendingEntries(
  counsel: VisitCounselView | null | undefined
): AdditionalSpacePendingEntry[] {
  if (!counsel) return [];
  const sorted = sortAdditionalSpaceObservingSessions(
    counsel.sessions?.additionalSpaceObserving ?? []
  );
  const out: AdditionalSpacePendingEntry[] = [];
  for (const session of sorted) {
    if (!isAdditionalSpacePending(session)) continue;
    out.push({
      sessionId: session.id,
      label: SPACE_TYPE_LABELS[session.spaceType] ?? session.spaceType,
      session,
      canRecordMovingItems: session.canRecordMovingItems === true,
      isFirstStart: session.status === 'REQUESTED',
    });
  }
  return out;
}

/** 단일 세션이 미완료(picker 노출 대상) 인지 판정. */
export function isAdditionalSpacePending(session: AdditionalSpaceObservingSession): boolean {
  if (!session.video) return true;
  if (session.canRecordMovingItems !== true) return false;
  if (!hasObservingSessionMovingItemsConfirmed(session)) return true;
  const items = session.movingItems ?? [];
  for (const it of items) {
    if (it.serviceSet == null) return true;
  }
  return false;
}
